import { useCallback, useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "pointermove",
] as const;

const STORAGE_KEY = "audinero_last_activity";
const CHANNEL_NAME = "audinero_idle_timeout";

interface UseIdleTimeoutOptions {
  /** Total timeout in ms (default: 2 hours) */
  timeoutMs?: number;
  /** Warning threshold in ms before timeout (default: 5 minutes) */
  warningMs?: number;
  /** Called when the session expires */
  onTimeout: () => void;
}

interface UseIdleTimeoutReturn {
  /** Whether the warning dialog should be shown */
  showWarning: boolean;
  /** Seconds remaining until timeout */
  secondsRemaining: number;
  /** Extend the session (reset the timer) */
  extendSession: () => void;
}

/**
 * Tracks user activity and triggers a timeout after prolonged inactivity.
 * Syncs across tabs via localStorage + BroadcastChannel.
 */
export function useIdleTimeout({
  timeoutMs = 2 * 60 * 60 * 1000, // 2 hours
  warningMs = 5 * 60 * 1000, // 5 minutes before timeout
  onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.floor(timeoutMs / 1000),
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const warningRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setSecondsRemaining(Math.floor(timeoutMs / 1000));

    // Persist last activity timestamp for cross-tab sync
    const now = Date.now();
    try {
      localStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // localStorage may be unavailable
    }

    // Show warning before timeout
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      let remaining = Math.floor(warningMs / 1000);
      setSecondsRemaining(remaining);

      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setSecondsRemaining(remaining);
        if (remaining <= 0) {
          clearInterval(countdownRef.current);
        }
      }, 1000);
    }, timeoutMs - warningMs);

    // Trigger timeout
    timeoutRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs, warningMs, clearAllTimers]);

  const extendSession = useCallback(() => {
    resetTimers();
    // Notify other tabs
    channelRef.current?.postMessage("extend");
  }, [resetTimers]);

  // Debounced activity handler — resets timers on user interaction
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    function handleActivity() {
      // Don't reset if warning is showing — user must explicitly extend
      if (showWarning) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        resetTimers();
      }, 1000);
    }

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      clearTimeout(debounceTimer);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [resetTimers, showWarning]);

  // Cross-tab sync via BroadcastChannel
  useEffect(() => {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = (event) => {
        if (event.data === "extend") {
          resetTimers();
        }
        if (event.data === "logout") {
          onTimeoutRef.current();
        }
      };

      return () => {
        channel.close();
        channelRef.current = null;
      };
    } catch {
      // BroadcastChannel not supported — fall back to storage events
    }
  }, [resetTimers]);

  // Fallback cross-tab sync via storage events (for browsers without BroadcastChannel)
  useEffect(() => {
    function handleStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY && e.newValue) {
        resetTimers();
      }
    }

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [resetTimers]);

  // Initialize timers on mount
  useEffect(() => {
    resetTimers();
    return clearAllTimers;
  }, [resetTimers, clearAllTimers]);

  return { showWarning, secondsRemaining, extendSession };
}
